import React, { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
	Alert,
	Box,
	Button,
	Chip,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";

const API = process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN;

interface Baseline {
	baselineID: number;
	year: number;
	scope1Tonnes: number;
	scope2Tonnes: number;
	scope3Tonnes: number;
	totalTonnes: number;
	version: number;
	isCurrent: number;
	restatementReason: string | null;
	createdAt: string;
}

/** Multi-year baselines: lock a reporting year, restate with a reason. */
const Baselines: React.FC = () => {
	const { getAccessTokenSilently } = useAuth0();
	const [rows, setRows] = useState<Baseline[]>([]);
	const [msg, setMsg] = useState<string | null>(null);
	const [year, setYear] = useState(String(new Date().getFullYear() - 1));
	const [reason, setReason] = useState("");
	const [busy, setBusy] = useState(false);

	const call = useCallback(
		async (path: string, init?: RequestInit) => {
			const token = await getAccessTokenSilently();
			const res = await fetch(`${API}${path}`, {
				...init,
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
				},
			});
			const data = await res.json().catch(() => ({}));
			return { ok: res.ok, data: data.Data || data };
		},
		[getAccessTokenSilently],
	);

	const load = useCallback(async () => {
		const r = await call("/api/baselines");
		if (r.ok) setRows(r.data.baselines || []);
	}, [call]);

	useEffect(() => {
		load();
	}, [load]);

	const act = async (path: string, body: object) => {
		setBusy(true);
		setMsg(null);
		try {
			const r = await call(path, {
				method: "POST",
				body: JSON.stringify(body),
			});
			setMsg(r.data.Message || r.data.message || "Done");
			if (r.ok) {
				setReason("");
				await load();
			}
		} finally {
			setBusy(false);
		}
	};

	return (
		<Box maxWidth={900} margin="0 auto" padding={3}>
			<Typography variant="h5" gutterBottom>
				Emissions baselines
			</Typography>
			<Typography variant="body2" color="text.secondary" gutterBottom>
				Lock a reporting year to freeze its Scope 1, 2 and 3 totals.
				Locked years can only change through a restatement with a
				documented reason — every prior version is kept for assurance.
			</Typography>

			{msg && (
				<Alert severity="info" sx={{ my: 2 }}>
					{msg}
				</Alert>
			)}

			<Paper
				elevation={0}
				sx={{ p: 2, my: 2, border: "1px solid rgba(0,23,28,0.10)" }}
			>
				<Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
					<TextField
						label="Year"
						size="small"
						value={year}
						onChange={(e) => setYear(e.target.value)}
						sx={{ width: 110 }}
					/>
					<Button
						variant="contained"
						color="secondary"
						startIcon={<LockIcon />}
						disabled={busy}
						onClick={() => act("/api/baselines", { year })}
					>
						Lock year
					</Button>
					<TextField
						label="Restatement reason"
						size="small"
						value={reason}
						onChange={(e) => setReason(e.target.value)}
						sx={{ minWidth: 280, flexGrow: 1 }}
					/>
					<Button
						variant="outlined"
						color="secondary"
						disabled={busy || reason.trim().length < 10}
						onClick={() =>
							act("/api/baselines/restate", { year, reason })
						}
					>
						Restate year
					</Button>
				</Box>
			</Paper>

			<Paper
				elevation={0}
				sx={{ p: 2, my: 2, border: "1px solid rgba(0,23,28,0.10)" }}
			>
				{rows.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						No locked baselines yet.
					</Typography>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Year</TableCell>
								<TableCell>v</TableCell>
								<TableCell>Scope 1</TableCell>
								<TableCell>Scope 2</TableCell>
								<TableCell>Scope 3</TableCell>
								<TableCell>Total (t CO2e)</TableCell>
								<TableCell>Status</TableCell>
								<TableCell>Reason</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{rows.map((b) => (
								<TableRow key={b.baselineID}>
									<TableCell>{b.year}</TableCell>
									<TableCell>{b.version}</TableCell>
									<TableCell>{b.scope1Tonnes}</TableCell>
									<TableCell>{b.scope2Tonnes}</TableCell>
									<TableCell>{b.scope3Tonnes}</TableCell>
									<TableCell>{b.totalTonnes}</TableCell>
									<TableCell>
										{b.isCurrent ? (
											<Chip
												label="current"
												size="small"
												color="secondary"
											/>
										) : (
											<Chip
												label="superseded"
												size="small"
											/>
										)}
									</TableCell>
									<TableCell>
										{b.restatementReason || "—"}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</Paper>
		</Box>
	);
};

export default Baselines;
